import { computed, action, observable } from 'mobx';
import { network } from '@tencent/netcheck';
import SnackbarPc from '@tencent/dui/lib/components/Snackbar';
import SnackbarM from '@tencent/dui-mobile/lib/components/Snackbar';
import i18n from '@i18next/i18n';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import PicUrlCacheManager from '@editor/src/page/index/components/canvas/element-types/shape-element/addon/PicUrlCacheManager';
import { key2md5 } from '@editor/src/page/index/components/canvas/element-types/shape-element/addon/utils';
import { IMediaManager, MediaRegion, PreservedAudio, VideoType, WebVideoPlayer } from './media-manager.interface';
import { isAttachmentImageElement, isDomGifPictureModel, isGroupModel } from '@src/slide/domain/ui-model/elements/element-judge';
import type AnimationModel from '@src/slide/domain/ui-model/element-modeles/anim-model';
import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { isSpecialActivity } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { PlayerInstance } from '@src/framework/editor/src/page/index/components/canvas/element-types/image-element/media-container/wcjs-player-instance';
import AttachmentUploader from '@src/js/common/ark-extensions/dc-components/attachment-uploader';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { PlayerType } from '../note-view-media-sync-manager/core/note-view-media-sync-manager.interface';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { EStatusEvents } from '@src/js/common/ark-extensions/dc-components/attachment-uploader/type';
import { IAttachmentManager } from '@src/slide/application/service/attachment-upload/attachment-manager.interface';
import { TRANSPARENT_PIXEL_BASE64 } from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';

export enum SelectedGifHandler {
    'ADD' = 'add',
    'DELETE' = 'delete',
}

interface IMediaManagerProps {
    attachmentManager?: IAttachmentManager;
}

// 图片附件封面图缓存时长
const IMAGE_ATTACHMENT_POSTER_CACHE_DURATION = 1000 * 60;

const snackBar = (browser.isMobile ? SnackbarM : SnackbarPc) as unknown as typeof SnackbarPc;

export default class MediaManager implements IMediaManager {
    // 用来标志编辑态音视频元素是否需要显示为 dom 状态
    @observable public elementKey: string | null = null;
    @observable public isEditMode = false;
    /* --- 视频 --- */
    // 编辑态下的videoInstances
    public videoInstances = observable(new Map<string, VideoType | null>(), { deep: false });
    // 演示态下的videoInstancesInPlayer
    public videoInstancesInPlayer = observable(new Map<string, VideoType | null>(), { deep: false });
    @observable public isVideoFullScreen = false;
    /* --- 音频 --- */
    // 音频状态
    public audioInstances = observable(new Map<string, HTMLAudioElement>(), { deep: false });
    public audioInstancesInPlayer = observable(new Map<string, HTMLAudioElement>(), { deep: false });

    /* --- iframe --- */
    // iframe 状态
    @observable public prevIframeSelected = false;
    @observable public currentIframeSelected = false;
    // iframe 封面
    @observable public iframePoster: string | null = null;
    @observable public iframePosterCaches = new Map<string, string>();

    /* --- gif --- */
    // 正在播放动画的 gif 元素 key
    @observable public animatingGif = new Set<string>();

    /* ================================ 演示态数据 ================================ */
    // 演示态下已经完成播放动画的 gif 元素 key
    @observable public playerAnimationFinishedGif = new Set<string>();
    @observable public playerAnimatingGif = new Set<string>();

    @observable public thumbnailElementKey = '';


    @observable
    public notifyGifScaleUpdate = {};

    // 用来标识演示态中哪些音视频或 iframe 插件元素需要切换为 dom 状态
    @observable private playerMediaElementKeys = new Set<string>();

    // 移动端预览态的音视频实例，用于手动停止播放
    // 主要是因为存在音视频组件不销毁但是要停止播放的情况（比如移动端预览态进入演示之后，原来预览态所有元素都不销毁只是隐藏）
    private audioInstancesInMobilePreview = new Map<string, HTMLAudioElement>();
    private videoInstancesInMobilePreview = new Map<string, VideoType>();
    private elementUnSelectedCallback: (() => void) | null;

    // 需要保留的音频实例
    private preserveAudio = new Map<string, PreservedAudio>();
    // 图片附件缓存
    private imageAttachmentPosterUrlMap = new Map<string, string>();

    // 保存音频实例对应的待办，在实例被添加时执行回调
    private audioInstancePending = new Map<string, (audio: HTMLAudioElement) => void>();

    private attachmentManager?: IAttachmentManager;

    public constructor(props: IMediaManagerProps) {
        this.attachmentManager = props?.attachmentManager;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public fetchIframePoster = async (iframeModel) => {
        const { originUrl, embedUrl, iframeId, extraData } = iframeModel.props.addon;
        const { width, height } = iframeModel.props.style;
        try {
            const iframePoster = await PicUrlCacheManager.getInstance().getScreenshotImage(
                { originUrl, embedUrl, iframeId, extraData },
                key2md5({ originUrl, embedUrl }),
                width, height,
            );
            this.iframePosterCaches.set(iframeModel.key, iframePoster);
        } catch (error) {
            window.log(error);
        }
    };

    /**
     * 请求图片附件的封面图
     * 如果有缓存，直接返回，否则异步从 dc 接口中获取
     * @param model
     * 如果 model 不是图片附件，返回 null
     * 如果有缓存的地址，返回 url
     * 否则返回 promise，promise 会在请求到图片地址后 resolve
     * @returns string | null | Promise<string | null>
     */
    public fetchImageAttachmentPoster = (model): string | null | Promise<string | null> => {
        if (!isAttachmentImageElement(model)) {
            return null;
        }
        const poster = this.imageAttachmentPosterUrlMap.get(model.key);
        if (poster) {
            return poster;
        }

        // todo: 标记下，uploader的import直接占用了viewable-layer一半的耗时，改成异步
        const attachmentUploader = AttachmentUploader.getInstance();
        const getMediaPoster = async () => {
            try {
                const { dcAttachmentModule } = attachmentUploader;
                const posterUrl: string | null = await dcAttachmentModule.getMediaPoster({
                    objKey: model.props.attachment.objKey!,
                    // TODO 本地编辑没有padId导致组件无法渲染预览图(cPadId)
                    fileId: SlideDataServices.DocInfoManager()?.padId ?? '',
                });
                if (!posterUrl) throw new Error('attachment poster is null');
                const { key } = model;
                this.imageAttachmentPosterUrlMap.set(key, posterUrl);
                // 一分钟后清除缓存，因为 dc 给的图片地址是临时的，加了缓存避免无用的多次请求，同时减少图片失效的可能性
                setTimeout(() => {
                    this.imageAttachmentPosterUrlMap.delete(key);
                }, IMAGE_ATTACHMENT_POSTER_CACHE_DURATION);
                return posterUrl;
            } catch (error) {
                const { key } = model;
                this.imageAttachmentPosterUrlMap.set(key, TRANSPARENT_PIXEL_BASE64);
                // 一分钟后清除缓存，因为 dc 给的图片地址是临时的，加了缓存避免无用的多次请求，同时减少图片失效的可能性
                setTimeout(() => {
                    this.imageAttachmentPosterUrlMap.delete(key);
                }, IMAGE_ATTACHMENT_POSTER_CACHE_DURATION);
                return null;
            }
        };
        if (attachmentUploader.hasInit) {
            return getMediaPoster();
        }
        return new Promise<string | null>((resolve) => {
            attachmentUploader.addUploadListener({
                onUpLoaderInit: () => {
                    getMediaPoster().then(resolve);
                },
            });
        });
    };

    @computed public get isShowIframe(): boolean {
        return this.prevIframeSelected && this.currentIframeSelected;
    }

    public shouldRenderGifPoster(key: string): boolean {
        return this.animatingGif.has(key);
    }

    public shouldRenderDomGif(key: string): boolean {
        return !this.shouldRenderGifPoster(key);
    }

    public shouldPlayerRenderGifPoster(key: string): boolean {
        return this.playerAnimatingGif.has(key) && !this.playerAnimationFinishedGif.has(key);
    }

    public shouldPlayerRenderDomGif(key: string): boolean {
        return this.playerAnimationFinishedGif.has(key);
    }

    public onInitAnimStyles(model: any, isEntr: boolean): void {
        // gif 特殊逻辑，初始化当前页面时，所有的元素的状态应该都是未播放过动画，且没有正在播放动画的
        this.invokeCallbackWithGifElementModelKey(model, (key: string) => {
            if (isEntr) {
                // 如果初始为退出动画，则初始状态不应该隐藏 dom
                this.playerAnimationFinishedGif.delete(key);
            } else {
                this.playerAnimationFinishedGif.add(key);
            }
            this.playerAnimatingGif.delete(key);
        });
    }

    public handleAnimationStart(elementModel: any): void {
        this.invokeCallbackWithGifElementModelKey(elementModel, (key: string) => {
            this.animatingGif.add(key);
        });
    }

    public handleAnimationEnd(elementModel: any): void {
        this.invokeCallbackWithGifElementModelKey(elementModel, (key: string) => {
            this.animatingGif.delete(key);
        });
    }

    public handlePlayerAnimationStart(elementModel: any): void {
        this.invokeCallbackWithGifElementModelKey(elementModel, (key: string) => {
            this.playerAnimationFinishedGif.delete(key);
            this.playerAnimatingGif.add(key);
        });
    }

    public handlePlayerAnimationEnd(elementModel: any, animationModel: AnimationModel): void {
        const isExit = animationModel.props.presetClass === presetClassTypes.EXIT;
        const isEmph = animationModel.props.presetClass === presetClassTypes.EMPH;
        this.invokeCallbackWithGifElementModelKey(elementModel, (key: string) => {
            if (!isExit) {
                // 如果是退出类型动画，则同样不渲染 dom
                this.playerAnimationFinishedGif.add(key);
            }
            this.playerAnimatingGif.delete(key);
            if (isEmph && animationModel.props.by) {
                const { x, y } = animationModel.props.by;
                this.notifyGifScaleUpdate = {
                    key: elementModel.key,
                    widthScale: x ?? 1,
                    heightScale: y ?? 1,
                };
            }
        });
    }

    public setPlayerGifAnimationFinishedState(model: any): void {
        this.invokeCallbackWithGifElementModelKey(model, (key: string) => {
            this.playerAnimationFinishedGif.add(key);
        });
    }

    public handlePlayEnd(): void {
        this.playerAnimationFinishedGif.clear();
        this.playerAnimatingGif.clear();
    }


    @action
    public updateActiveThumbnailMedia(key: string, callback: () => void): void {
        this.thumbnailElementKey = key;
        this.elementUnSelectedCallback = callback;
    }

    @action
    public removeActiveThumbnailMedia(): void {
        if (this.thumbnailElementKey) {
            this.elementUnSelectedCallback?.();
            this.elementUnSelectedCallback = null;
            this.thumbnailElementKey = '';
        }
    }

    @action
    public updateIframeSelectState(isSelected: boolean): void {
        this.prevIframeSelected = this.currentIframeSelected;
        this.currentIframeSelected = isSelected;
    }

    /**
     * 改变 iframe 展示状态，调用一次即可为 dom 元素
     * @param isSelected
     */
    @action
    public changeIframeDisplayState(isSelected: boolean): void {
        this.prevIframeSelected = isSelected;
        this.currentIframeSelected = isSelected;
    }

    @action
    public resetIframeSelectedState(): void {
        this.prevIframeSelected = false;
        this.currentIframeSelected = false;
    }


    public updateElementKey(key: string | null): void {
        this.elementKey = key;
    }

    /**
     * 添加正在播放的元素
     * @param elementKey
     * @param region
     */
    public updateElementKeyByRegion(elementKey: string, region: MediaRegion) {
        if (region === MediaRegion.EDITOR) {
            this.updateElementKey(elementKey);
        }
        if (region === MediaRegion.PLAYER) {
            this.addPlayerMediaElementKey(elementKey);
        }
    }

    /**
     * 如果传入的音视频元素是在 dom 态，切换回 canvas 状态
     * @param elementKey
     * @param region
     */
    public removeElementKeyByRegion(elementKey: string, region: MediaRegion) {
        if (this.elementKey === elementKey && region === MediaRegion.EDITOR) {
            this.updateElementKey(null);
        }
        if (this.shouldShowPlayerMediaElement(elementKey) && region === MediaRegion.PLAYER) {
            this.deletePlayerMediaElementKey(elementKey);
        }
    }

    /**
     * 演示态切换当前音视频或 iframe 插件为 dom 状态
     * @param key
     */
    public addPlayerMediaElementKey(key: string): void {
        this.playerMediaElementKeys.add(key);
    }

    public clearPlayerMediaElementKey(): void {
        this.playerMediaElementKeys.clear();
    }

    /**
     * 判断当前音视频或 iframe 插件是否应该显示为 dom 状态
     * @param elementKey
     * @param elementId
     * @param region
     * @returns
     */
    public shouldShowMediaElement(elementKey: string, elementId: string, region: MediaRegion) {
        if (region === MediaRegion.EDITOR) {
            return this.elementKey === elementKey
                || this.attachmentManager?.getUploadStatus(elementId) === EStatusEvents.ERROR;
        }
        if (region === MediaRegion.PLAYER) {
            return this.shouldShowPlayerMediaElement(elementKey)
                || this.attachmentManager?.getUploadStatus(elementId) === EStatusEvents.ERROR;
        }
        if (region === MediaRegion.MOBILE_PREVIEW) {
            return true;
        }
        return false;
    }

    public getVideoInstanceByRegion(elementKey: string, region: MediaRegion): VideoType | null {
        const videoMap = {
            [MediaRegion.EDITOR]: this.videoInstances,
            [MediaRegion.PLAYER]: this.videoInstancesInPlayer,
            [MediaRegion.MOBILE_PREVIEW]: this.videoInstancesInMobilePreview,
        };
        return videoMap[region]?.get(elementKey) ?? null;
    }
    public setVideoInstancesByRegion(elementKey: string, videoInstance: VideoType, region: MediaRegion): void {
        if (region === MediaRegion.EDITOR) {
            this.videoInstances.set(elementKey, videoInstance);
        }

        if (region === MediaRegion.PLAYER) {
            this.videoInstancesInPlayer.set(elementKey, videoInstance);
        }

        if (region === MediaRegion.MOBILE_PREVIEW) {
            this.videoInstancesInMobilePreview.set(elementKey, videoInstance);
        }
    }
    public deleteVideoInstanceByRegion(elementKey: string, region: MediaRegion): void {
        this.pauseVideoByRegion(elementKey, region);
        if (region === MediaRegion.EDITOR) {
            this.videoInstances.delete(elementKey);
        }

        if (region === MediaRegion.PLAYER) {
            this.videoInstancesInPlayer.delete(elementKey);
        }

        if (region === MediaRegion.MOBILE_PREVIEW) {
            this.videoInstancesInMobilePreview.delete(elementKey);
        }
    }

    public getPreserveAudioByKey(key: string): PreservedAudio | undefined {
        return this.preserveAudio.get(key);
    }

    public setPreserveAudioByKey(key: string, preservedAudio: PreservedAudio): void {
        this.preserveAudio.set(key, preservedAudio);
    }

    public deletePreserveAudioByKey(key: string): void {
        this.preserveAudio.delete(key);
    }

    public setAudioInstanceByRegion(elementKey: string, audioInstance: HTMLAudioElement, region: MediaRegion): void {
        if (region === MediaRegion.EDITOR) {
            this.audioInstances.set(elementKey, audioInstance);
        }

        if (region === MediaRegion.PLAYER) {
            this.audioInstancesInPlayer.set(elementKey, audioInstance);
            // 执行对应音频实例的待执行逻辑
            this.audioInstancePending.get(elementKey)?.(audioInstance);
        }

        if (region === MediaRegion.MOBILE_PREVIEW) {
            this.audioInstancesInMobilePreview.set(elementKey, audioInstance);
        }
    }

    public getAudioInstanceByRegion(elementKey: string, region: MediaRegion): HTMLAudioElement | null {
        const audioMap = {
            [MediaRegion.EDITOR]: this.audioInstances,
            [MediaRegion.PLAYER]: this.audioInstancesInPlayer,
            [MediaRegion.MOBILE_PREVIEW]: this.audioInstancesInMobilePreview,
        };
        return audioMap[region].get(elementKey) || null;
    }

    public deleteAudioInstanceByRegion(elementKey: string, region: MediaRegion, needCheckPreserveState = false): void {
        this.resetAudioByRegion(elementKey, region, needCheckPreserveState);
        if (region === MediaRegion.EDITOR) {
            this.audioInstances.delete(elementKey);
        }

        if (region === MediaRegion.PLAYER) {
            // 需要check保留状态且在preserveAudio中找到了对应audio的时候才不删，其他情况都删
            if (!(needCheckPreserveState && this.preserveAudio.get(elementKey))) {
                this.audioInstancesInPlayer.delete(elementKey);
            }
        }

        if (region === MediaRegion.MOBILE_PREVIEW) {
            this.audioInstancesInMobilePreview.delete(elementKey);
        }
    }

    public playVideoByRegion(elementKey: string, region: MediaRegion): void {
        if (this.handleOfflineToast()) {
            return;
        }
        if (region === MediaRegion.EDITOR) {
            this.updateElementKey(elementKey);
            if (this.videoInstances.has(elementKey)) {
                const video = this.videoInstances.get(elementKey);
                this.playVideo(video);
                return;
            }
            // 添加标志，这样 shouldVideoRender 就会返回 true，就可以渲染出 dc 视频组件，从而实现播放
            this.videoInstances.set(elementKey, null);
        }

        if (region === MediaRegion.PLAYER) {
            this.addPlayerMediaElementKey(elementKey);
            if (this.videoInstancesInPlayer.has(elementKey)) {
                const video = this.videoInstancesInPlayer.get(elementKey);
                this.playVideo(video);
                return;
            }
            // 添加标志，这样 shouldVideoRender 就会返回 true，就可以渲染出 dc 视频组件，从而实现播放
            this.videoInstancesInPlayer.set(elementKey, null);
        }
    }

    public playAudioByRegion(elementKey: string, region: MediaRegion): void {
        if (this.handleOfflineToast()) {
            return;
        }
        if (region === MediaRegion.EDITOR) {
            this.updateElementKey(elementKey);
            if (this.audioInstances.has(elementKey)) {
                this.audioInstances.get(elementKey)?.play();
                return;
            }
        }
        if (region === MediaRegion.PLAYER) {
            this.addPlayerMediaElementKey(elementKey);
            const audio = this.audioInstancesInPlayer.get(elementKey);
            if (audio) {
                // 当前音频已加载，直接播放
                audio.play();
            } else {
                // 没加载，等待加载完成后播放，自动播放的音频在演示态可能会出现这种情况
                this.audioInstancePending.set(elementKey, (audio: HTMLAudioElement) => {
                    audio.addEventListener('canplay', () => {
                        // 等待 canplay 后再播放是为了保证 dc 中能正确更新播放状态
                        audio.play();
                    }, { once: true });
                    // 执行完删除当前 pending
                    this.audioInstancePending.delete(elementKey);
                });
            }
        }
    }

    public pauseVideoByRegion(elementKey: string, region: MediaRegion) {
        const video = this.getVideoInstanceByRegion(elementKey, region);
        video?.pause();
        this.removeElementKeyByRegion(elementKey, region);
    }

    public pauseAudioByRegion(elementKey: string, region: MediaRegion) {
        const audio = this.getAudioInstanceByRegion(elementKey, region);
        audio?.pause();
        this.removeElementKeyByRegion(elementKey, region);
    }

    public resetAudioByRegion(elementKey: string, region: MediaRegion, needCheckPreserveState = false) {
        const audio = this.getAudioInstanceByRegion(elementKey, region);
        /** 远程演示状态下 region 可能为 editor，media-element-container销毁时会触发暂停音频？
         * 暂时取消掉 region === MediaRegion.PLAYER 的判断，让 editor 状态下也能保留音频，todo：后续再考虑
        */
        if (!(this.preserveAudio.get(elementKey) && needCheckPreserveState)) {
            this.resetAudio(audio);
            this.removeElementKeyByRegion(elementKey, region);
        }
    }

    public resetVideoByRegion(elementKey: string, region: MediaRegion) {
        const video = this.getVideoInstanceByRegion(elementKey, region);
        this.resetVideo(video);
        this.removeElementKeyByRegion(elementKey, region);
    }

    /**
     * 因为视频组件渲染后就会直接播放，所以默认不能渲染
     * @param elementKey
     * @param elementId
     * @param region
     * @returns
     */
    public shouldVideoRender(elementKey: string, elementId: string, region: MediaRegion): boolean {
        const isUploadFail = this.attachmentManager?.getUploadStatus(elementId) === EStatusEvents.ERROR;

        if (region === MediaRegion.EDITOR) {
            return this.videoInstances.has(elementKey) || isUploadFail ;
        }
        if (region === MediaRegion.PLAYER) {
            return this.videoInstancesInPlayer.has(elementKey) || isUploadFail;
        }
        if (region === MediaRegion.MOBILE_PREVIEW) {
            // 移动端预览态默认显示视频组件
            return true;
        }
        return false;
    }

    /**
     * 停止所有编辑区的音视频播放
     */
    public resetAllEditorMedia() {
        this.audioInstances.forEach((audio) => {
            this.resetAudio(audio);
        });
        this.videoInstances.forEach((video) => {
            this.resetVideo(video);
        });
    }

    /**
     * 清除编辑态所有音视频实例
     */
    public disposeAllEditorMediaInstance() {
        this.resetAllEditorMedia();
        this.videoInstances.clear();
        this.audioInstances.clear();
    }

    /**
     * 停止所有演示态的音视频播放
     */
    public resetAllPlayerMedia(destroy = false) {
        this.audioInstancesInPlayer.forEach((audio) => {
            this.resetAudio(audio);
        });
        this.videoInstancesInPlayer.forEach((video) => {
            this.resetVideo(video, destroy);
        });
        this.preserveAudio.forEach((audio) => {
            this.resetAudio(audio.audioIns);
        });
    }

    /**
     * 停止所有演示态下不需要保留实例的音视频播放，无特殊场景调resetAllPlayerMedia即可
     */
    public resetAllPlayerMediaIfNeed(destroy = false) {
        this.audioInstancesInPlayer.forEach((audio, key) => {
            if (!this.preserveAudio.get(key)) {
                this.resetAudio(audio);
            }
        });
        this.videoInstancesInPlayer.forEach((video) => {
            this.resetVideo(video, destroy);
        });
    }

    /**
     * 清除演示态所有音视频实例
     * @param needCheckPreserveState 是否需要保留某些音视频的播放态
     */
    public disposeAllPlayerMediaInstance(needCheckPreserveState: boolean) {
        if (needCheckPreserveState) {
            this.resetAllPlayerMediaIfNeed(true);
            const needDeleteInsKeys: string[] = [];
            this.audioInstancesInPlayer.forEach((item, key) => {
                if (!this.preserveAudio.get(key)) {
                    needDeleteInsKeys.push(key);
                }
            });
            needDeleteInsKeys.forEach((key) => {
                this.audioInstancesInPlayer.delete(key);
            });
        } else {
            this.resetAllPlayerMedia(true);
            this.audioInstancesInPlayer.clear();
        }
        this.videoInstancesInPlayer.clear();
    }

    /**
     * 处理演示态下所有需要保留播放状态的音频状态
     * @param slideIndex 当前slide的index
     */
    public handlePreserveAudioState(slideIndex: number) {
        if (!slideIndex && slideIndex !== 0) {
            return;
        }
        const needDeleteInsKeys: string[] = [];
        this.preserveAudio.forEach((preserveAudio, key) => {
            if (slideIndex > preserveAudio.stopAtSlideIndex || slideIndex < preserveAudio.startAtSlideIndex) {
                needDeleteInsKeys.push(key);
            }
        });
        needDeleteInsKeys.forEach((key) => {
            const { audioIns } = this.preserveAudio.get(key)!;
            this.resetAudio(audioIns);
            this.deleteAudioInstanceByRegion(key, MediaRegion.PLAYER);
            this.preserveAudio.delete(key);
        });
    }

    /**
     * 清除移动端预览态所有音视频播放
     */
    public resetAllMobilePreviewMedia() {
        this.audioInstancesInMobilePreview.forEach((audio) => {
            this.resetAudio(audio);
        });
        this.videoInstancesInMobilePreview.forEach((video) => {
            this.resetVideo(video);
        });
    }

    // 在线文档离线场景不支持播放音视频
    private handleOfflineToast(): boolean {
        if (!network.isOnline && !ENV.IS_LOCAL_EDIT) {
            snackBar.show({
                message: i18n.t('当前处于离线环境，暂不支持预览此附件'),
                autoClose: true,
                duration: 2000,
            });
            return true;
        }
        return false;
    }

    private resetAudio(audio?: HTMLAudioElement | null) {
        if (!audio) {
            return;
        }
        // 运营活动需要断点续播
        if (!isSpecialActivity) {
            audio.currentTime = 0;
        }
        audio.pause();
    }

    private resetVideo(video?: VideoType | null, destroy = false) {
        if (!video) {
            return;
        }
        if ('stop' in video) {
            video.pause();
            video.stop();
        } else {
            video.pause();
        }
        if (destroy && 'destroy' in video) {
            video.destroy();
        }
    }

    /**
     * 从头播放视频
     * @param video
     * @returns
     */
    private playVideo(video?: VideoType | null) {
        if (!video) {
            return;
        }
        if (this.isWebVideo(video)) {
            video.replay();
        } else {
            (video as PlayerInstance).play(0);
        }
    }

    private isWebVideo(video?: VideoType | null): video is WebVideoPlayer {
        return video?.playerType === PlayerType.WEB;
    }


    /**
     * 对于一个传入的数据 uiModel，如果他自己（或者他的 children 中）为 gifModel，则调用传入回调
     * @param elementModel 一个数据 model 类
     * @param callback 方法会对传入的 elementModel 对应的 gifModel 调用此回调，参数为 gifModel 的 key 成员
     */
    private invokeCallbackWithGifElementModelKey(elementModel: any, callback: (key: string) => void): void {
        if (isDomGifPictureModel(elementModel)) {
            callback(elementModel.key);
        } else if (isGroupModel(elementModel)) {
            elementModel.getGifsInGroup().forEach((gifModel) => {
                callback(gifModel.key);
            });
        }
    }


    /**
     * 演示态切换当前音视频或 iframe 插件为 canvas 状态
     * @param key
     */
    private deletePlayerMediaElementKey(key: string): void {
        this.playerMediaElementKeys.delete(key);
    }

    /**
     * 判断演示态中当前音视频或 iframe 插件是否应该显示为 dom 状态
     * @param key
     * @returns
     */
    private shouldShowPlayerMediaElement(key: string): boolean {
        // 被保留了实例的 audio 也显示为 dom 状态（dom 状态会有旋转动画，canvas 无）
        return this.playerMediaElementKeys.has(key) || !!this.preserveAudio.get(key);
    }
}
